import type { Action, ContentBlock } from '@beidou/agent-protocol';
import ReactMarkdown from 'react-markdown';
import { BlockRenderer } from '../renderer/BlockRenderer';
import { isItemCard } from '../utils/cardTypes';

const DETAIL_INTRO_RE = /详情如下/;

function isDetailCard(block: ContentBlock): boolean {
  return isItemCard(block);
}

function isDetailIntro(block: ContentBlock): boolean {
  return block.type === 'markdown' && DETAIL_INTRO_RE.test(block.text);
}

function isDetailCta(block: ContentBlock): boolean {
  return block.type === 'button' && block.variant === 'primary';
}

type BlockSegment =
  | { kind: 'detail'; blocks: ContentBlock[] }
  | { kind: 'single'; block: ContentBlock };

export function segmentMessageBlocks(blocks: ContentBlock[]): BlockSegment[] {
  const segments: BlockSegment[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    const next = blocks[i + 1];
    const third = blocks[i + 2];

    if (block.type === 'markdown' && isDetailIntro(block) && next && isDetailCard(next)) {
      const group: ContentBlock[] = [block, next];
      if (third && isDetailCta(third)) {
        group.push(third);
        i += 3;
      } else {
        i += 2;
      }
      segments.push({ kind: 'detail', blocks: group });
      continue;
    }

    if (isDetailCard(block)) {
      const group: ContentBlock[] = [block];
      if (next && isDetailCta(next)) {
        group.push(next);
        i += 2;
      } else {
        i += 1;
      }
      segments.push({ kind: 'detail', blocks: group });
      continue;
    }

    segments.push({ kind: 'single', block });
    i += 1;
  }
  return segments;
}

function DetailPanel({
  blocks,
  onAction,
}: {
  blocks: ContentBlock[];
  onAction: (action: Action) => void;
}) {
  const intro = blocks.find((b) => b.type === 'markdown');
  const card = blocks.find(isDetailCard);
  const cta = blocks.find(isDetailCta);

  if (!card) return null;

  return (
    <div className="detail-panel">
      {intro && intro.type === 'markdown' && (
        <div className="detail-panel-intro">
          <ReactMarkdown>{intro.text}</ReactMarkdown>
        </div>
      )}
      <div className="detail-panel-body">
        <BlockRenderer block={card} onAction={onAction} variant="chat" heroSize="detail" />
      </div>
      {cta && cta.type === 'button' && (
        <div className="detail-panel-cta">
          <button type="button" className="action-btn primary detail-panel-btn" onClick={() => onAction(cta.action)}>
            {cta.label}
          </button>
        </div>
      )}
    </div>
  );
}

interface MessageBlocksProps {
  blocks: ContentBlock[];
  onAction: (action: Action) => void;
  markdownStreaming?: boolean;
}

export function MessageBlocks({ blocks, onAction, markdownStreaming }: MessageBlocksProps) {
  const segments = segmentMessageBlocks(blocks);
  return (
    <>
      {segments.map((segment, i) => {
        if (segment.kind === 'detail') {
          return <DetailPanel key={`detail-${i}`} blocks={segment.blocks} onAction={onAction} />;
        }
        const block = segment.block;
        return (
          <BlockRenderer
            key={`block-${i}`}
            block={block}
            onAction={onAction}
            markdownStreaming={markdownStreaming && block.type === 'markdown'}
          />
        );
      })}
    </>
  );
}
